import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ReactDOM from "react-dom";
import { CustomOptionItem, CustomRadio, CustomCheck } from "@components/dropdown/CustomOption";

type Props = {
  name: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  options: any[];
};

const PatternDropdown = ({ name, open, setOpen, options }: Props) => {
  const { setValue, watch } = useFormContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [customKo, setCustomKo] = useState("");
  const [customEn, setCustomEn] = useState("");
  const [customJa, setCustomJa] = useState("");
  const [customError, setCustomError] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);
  const theme = useTheme();
  const dropdownHeight = 256;
  const selected = watch(name);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (options.length === 0) return;
    setOpen(!open);
  };

  const handleChange = (patternName: string, patternId: number) => {
    setIsCustomMode(false);
    setOpen(false);
    setValue(name, { pattern_name: patternName, pattern_id: patternId });
  };

  const handleCustom = () => {
    const ko = customKo.trim();
    const en = customEn.trim();
    const ja = customJa.trim();

    // 명세: '직접 입력' 선택 시 패턴명 입력은 필수.
    if (!ko || !en || !ja) {
      setCustomError("패턴명은 한글·영문·일문 모두 입력해주세요.");
      return;
    }
    setCustomError("");
    setIsCustomMode(false);
    setValue(name, {
      pattern_id: null,
      pattern_name: [ko, en].filter(Boolean).join(" / "),
      name_ko: ko || null,
      name_en: en,
      name_ja: ja,
    }, { shouldValidate: true });
    setOpen(false);
  };

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.top + window.scrollY - dropdownHeight,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open]);

  return (
    <Container>
      <Button
        onClick={handleToggle}
        type="button"
        selectedValue={selected?.pattern_name || "선택"}
        ref={buttonRef}>
        {selected?.pattern_name || "선택"}
        <Image
          src={
            open
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>

      {open &&
        ReactDOM.createPortal(
          <SelectBox
            top={dropdownPos.top}
            left={dropdownPos.left}
            width={dropdownPos.width}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}>
            <CustomOptionItem
              type="button"
              selected={isCustomMode}
              onClick={() => setIsCustomMode((on) => !on)}>
              <CustomRadio selected={isCustomMode} />
              직접 입력 / Custom
              {isCustomMode && <CustomCheck>✓</CustomCheck>}
            </CustomOptionItem>

            {!isCustomMode &&
              options.map((item, index) => (
                <OptionItem
                  type="button"
                  key={item.id}
                  last={index === options.length - 1}
                  onClick={() => handleChange(item.name, item.id)}>
                  {item.name}
                </OptionItem>
              ))}

            {isCustomMode && (
            <CustomSection>
              <CustomInput value={customKo} onChange={(event) => setCustomKo(event.target.value)} placeholder="직접 패턴 (한글) *" />
              <CustomInput value={customEn} onChange={(event) => setCustomEn(event.target.value)} placeholder="Custom pattern (English) *" />
              <CustomInput value={customJa} onChange={(event) => setCustomJa(event.target.value)} placeholder="パターン名 (日本語) *" />
              <CustomButton type="button" onClick={handleCustom}>직접 입력 적용</CustomButton>
              {customError && <CustomError role="alert">{customError}</CustomError>}
            </CustomSection>
            )}
          </SelectBox>,
          document.body,
        )}
    </Container>
  );
};

export default PatternDropdown;

const Container = styled.div({
  position: "relative",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string }>(
  ({ theme, selectedValue }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",
    border: `1px solid ${theme.color.light_gray}`,
    color:
      selectedValue === "선택" || selectedValue === "Select"
        ? theme.color.light_gray
        : theme.color.black,
  }),
);

const SelectBox = styled(motion.div)<{
  top: number;
  left: number;
  width: number;
}>(({ theme, top, left, width }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "absolute",
  top: `${top}px`,
  left: `${left}px`,
  width: `${width}px`,
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
}));

const OptionItem = styled.button<{ last: boolean }>(({ theme, last }) => ({
  width: "100%",
  padding: "10px 30px 10px 20px",
  display: "flex",
  gap: "1rem",
  alignItems: "center",
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
  fontWeight: theme.fontWeight.regular,
  color: theme.color.black,
  backgroundColor: theme.color.white,
  border: "none",
  textAlign: "start",
  borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
  ":hover": {
    backgroundColor: theme.color.border_gray,
  },
}));

const CustomSection = styled.div({ display: "flex", flexDirection: "column", gap: "0.8rem", width: "100%", padding: "1.2rem" });
const CustomInput = styled.input(({ theme }) => ({ width: "100%", padding: "0.9rem", border: `1px solid ${theme.color.border_gray}` }));
const CustomButton = styled.button(({ theme }) => ({ padding: "1rem", border: "none", background: theme.color.black, color: theme.color.white }));

const CustomError = styled.p({ margin: 0, fontSize: "1.1rem", color: "#FF4808" });
